/**
 * Lädt das JavaScript-SDK des ONLYOFFICE Document Servers nach.
 *
 * Es kann nicht gebündelt werden: das SDK muss von DEM Server kommen, der die
 * Dokumente auch bearbeitet — Version und Server müssen zusammenpassen. Die
 * Adresse steht deshalb erst zur Laufzeit fest (sie kommt aus der
 * Editor-Konfiguration des Backends) und nicht zur Bauzeit.
 *
 * Einmal geladen, bleibt es geladen: das Versprechen wird je Adresse gemerkt,
 * damit zwei gleichzeitig geöffnete Dokumente nicht zwei Skript-Tags erzeugen.
 */

/** Die vom SDK gesetzte Globale. Absichtlich schmal — mehr braucht der Aufruf nicht. */
export interface DocsApiEditor {
    destroyEditor: () => void;
}

export interface DocsApi {
    DocEditor: new (placeholderId: string, config: Record<string, unknown>) => DocsApiEditor;
}

declare global {
    interface Window {
        DocsAPI?: DocsApi;
    }
}

const pending = new Map<string, Promise<DocsApi>>();

/** Nur für Tests — gemerkte Ladevorgänge verwerfen. */
export function resetOfficeApiCache(): void {
    pending.clear();
}

export function loadOfficeApi(documentServerUrl: string): Promise<DocsApi> {
    const base = documentServerUrl.replace(/\/+$/, "");
    const src = `${base}/web-apps/apps/api/documents/api.js`;

    const bereits = pending.get(src);
    if (bereits) return bereits;

    const promise = new Promise<DocsApi>((resolve, reject) => {
        if (window.DocsAPI) {
            resolve(window.DocsAPI);
            return;
        }

        const script = document.createElement("script");
        script.src = src;
        script.async = true;
        script.onload = () => {
            if (window.DocsAPI) {
                resolve(window.DocsAPI);
            } else {
                // Geladen, aber nichts gesetzt: das passiert, wenn der Pfad auf
                // eine andere Datei zeigt (z. B. eine nginx-Fehlerseite mit
                // Status 200). Ein eigener Fehler ist ehrlicher als ein späteres
                // „DocsAPI is not defined" an ganz anderer Stelle.
                reject(new Error("Der Office-Editor hat sich nicht angemeldet."));
            }
        };
        script.onerror = () => reject(new Error("Der Office-Editor ist nicht erreichbar."));
        document.head.appendChild(script);
    });

    // Ein FEHLGESCHLAGENER Ladevorgang darf nicht gemerkt werden — sonst bliebe
    // der Editor bis zum Neuladen der Seite kaputt, auch wenn der Document
    // Server zwei Sekunden später wieder da ist.
    promise.catch(() => pending.delete(src));

    pending.set(src, promise);
    return promise;
}
